import { h as __h } from "preact";
import { Component } from "preact";
/** @import { ComponentChildren } from "preact"; */

import html from "~/util/html.js";
/** @import { Placement } from "~/util/float.js"; */
import { popover } from "~/util/popover.js";
import FocusGroup from "./FocusGroup.js";
import Icon from "./Icon.js";
import Popover from "./Popover.js";
import Tooltip from "./Tooltip.js";
/** @import { TriggerProps } from "./Tooltip.js"; */

import util from "~/style/util.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./Dropdown.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./Dropdown.module.css", import.meta.url);
const css = adopt(sheet);

/**
 * @typedef {object} DropdownItem
 * @property {string} [icon]
 * @property {ComponentChildren} text
 * @property {ComponentChildren} [tooltip]
 * @property {string} [href]
 * @property {string} [target]
 * @property {() => void} [onSelect]
 * @property {boolean} [checked]
 * @property {boolean} [disabled]
 * @property {"negative" | "primary"} [kind]
 */

/**
 * @typedef {object} Props
 * @property {string} id
 * @property {ComponentChildren} trigger
 * @property {DropdownItem[]} items
 * @property {Placement} placement
 */

/** @extends {Component<Props>} */
export default class Dropdown extends Component {
  /** @param {PointerEvent & { currentTarget: HTMLDivElement }} ev */
  #onClick = ev => {
    if (!(ev.target instanceof Element)) return;
    if (!ev.target.closest("a, button, input")) return;
    ev.currentTarget.hidePopover();
  };
  render() {
    return __h(Popover, {
      id: this.props.id,
      trigger: this.props.trigger,
      placement: this.props.placement ?? "bottom-end",
      onClick: this.#onClick
    }, __h(DropdownMenu, {
      items: this.props.items
    }));
  }
}

/**
 * Opens a dropdown menu anchored to `anchor`, like `popover()`. Settles with the selected item, or
 * undefined if the menu is dismissed.
 *
 * @template {DropdownItem} I
 * @param {object} options
 * @param {HTMLElement} options.anchor
 * @param {Placement} options.placement
 * @param {I[]} options.items
 * @returns {Promise<I | undefined>}
 */
export function dropdown({
  anchor,
  placement,
  items
}) {
  return popover({
    anchor,
    placement,
    content: (/** @type {(result?: I) => void} */resolve) => __h(DropdownMenu, {
      items: items.map(item => ({
        ...item,
        onSelect: () => resolve(item)
      }))
    })
  });
}

/**
 * @extends {Component<MenuProps>}
 *
 * @typedef {object} MenuProps
 * @property {DropdownItem[]} items
 */
class DropdownMenu extends Component {
  render() {
    let hasIcons = false;
    const items = this.props.items.map((item, i) => {
      const checkbox = item.checked !== undefined;
      if (item.icon || checkbox) hasIcons = true;
      const icon = checkbox ? item.checked && "checkmark" : item.icon;
      const iconHtml = icon ? __h("span", {
        "class": css.icon
      }, __h(Icon, {
        name: icon,
        size: 12
      })) : null;
      const tag = checkbox ? "label" : item.href ? "a" : item.onSelect ? "button" : "div",
        type = tag === "button" ? "button" : undefined;
      const tagHtml = __h(tag, {
        key: i,
        "class": css.item,
        href: item.href,
        target: item.href ? item.target : undefined,
        rel: item.href && item.target === "_blank" ? "noopener" : undefined,
        onClick: checkbox ? undefined : item.onSelect,
        type: type,
        disabled: checkbox ? undefined : item.disabled,
        "data-kind": item.kind
      }, checkbox && __h("input", {
        type: "checkbox",
        "class": util.visuallyHidden,
        checked: item.checked,
        disabled: item.disabled,
        onChange: () => item.onSelect?.()
      }), iconHtml, __h("span", {
        "class": css.label
      }, item.text));
      if (item.tooltip) {
        return __h(Tooltip, {
          contents: item.tooltip,
          placement: "right"
        }, (/** @type {TriggerProps} */trigger) => __h("li", trigger, tagHtml));
      }
      return __h("li", {}, tagHtml);
    });
    return __h(FocusGroup, {
      selector: "a, button, input",
      direction: "vertical",
      loop: true
    }, __h("menu", {
      "class": css.items,
      "data-icons": hasIcons
    }, items));
  }
}